import React from "react";
import "./HeroLanding.css";
import logo from "./pin-maker-logo.png";

/**
 * Landing hero PIN Maker Studio.
 *
 * Pass callbacks from the existing app so the buttons use the app's current
 * authentication flow instead of replacing the existing login/register logic.
 *
 * Example:
 * <HeroLanding
 *   onLogin={() => setAuthMode("login")}
 *   onRegister={() => setAuthMode("register")}
 *   onStart={() => setAuthMode("login")}
 *   onFeatures={() => document.getElementById("fitur")?.scrollIntoView({ behavior: "smooth" })}
 * />
 */
export default function HeroLanding({
  onLogin,
  onRegister,
  onStart,
  onFeatures,
}) {
  // PMS_LANGUAGE_THEME_V1
  const [language, setLanguage] = React.useState(() => {
    try {
      return window.localStorage.getItem("pms-language") === "en" ? "en" : "id";
    } catch {
      return "id";
    }
  });

  const [theme, setTheme] = React.useState(() => {
    try {
      return window.localStorage.getItem("pms-theme") === "light" ? "light" : "dark";
    } catch {
      return "dark";
    }
  });

  // PMS_COOKIE_CONSENT_STATE_V1
  const [cookieConsent, setCookieConsent] = React.useState(() => {
    try {
      const saved = window.localStorage.getItem("pms-cookie-consent");
      return saved === "all" || saved === "essential" ? saved : null;
    } catch {
      return null;
    }
  });

  const saveCookieConsent = (choice) => {
    try {
      window.localStorage.setItem("pms-cookie-consent", choice);
    } catch {
      // Persetujuan tetap ditutup selama sesi ini jika penyimpanan tidak tersedia.
    }
    setCookieConsent(choice);
  };


  // PMS_TEMPLATE_SELECTION_STATE_V1
  const [selectedTemplate, setSelectedTemplate] = React.useState(() => {
    try {
      const saved = window.localStorage.getItem("pms-selected-template-size");
      return ["25", "32", "44", "58", "75"].includes(saved) ? saved : "44";
    } catch {
      return "44";
    }
  });

  React.useEffect(() => {
    try {
      window.localStorage.setItem("pms-language", language);
      window.localStorage.setItem("pms-theme", theme);
      window.localStorage.setItem("pms-selected-template-size", selectedTemplate);
    } catch {
      // Tetap berfungsi meskipun penyimpanan browser tidak tersedia.
    }
  }, [language, theme, selectedTemplate]);

  // PMS_SINGLE_SECTION_NAV_V1
  const validSections = ["beranda", "fitur", "template", "cara-kerja", "harga", "bantuan", "tentang", "privasi", "persyaratan"];
  const [activeSection, setActiveSection] = React.useState(() => {
    const hash = window.location.hash.replace("#", "");
    return validSections.includes(hash) ? hash : "beranda";
  });

  const navigateSection = (section) => {
    if (!validSections.includes(section)) return;
    setActiveSection(section);
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${window.location.search}#${section}`
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // PMS_SHARED_THEME_SYNC_HOME_V1
  React.useEffect(() => {
    const syncTheme = (event) => {
      const next = event?.detail?.theme;
      if (next === "dark" || next === "light") {
        setTheme(current => current === next ? current : next);
      }
    };
    window.addEventListener("pms-theme-change", syncTheme);
    return () => window.removeEventListener("pms-theme-change", syncTheme);
  }, []);

  // PMS_SHARED_THEME_BROADCAST_HOME_V1
  React.useEffect(() => {
    try {
      window.localStorage.setItem("pms-theme", theme);
      window.localStorage.setItem("pinmaker-login-theme", theme);
    } catch {}
    window.dispatchEvent(
      new CustomEvent("pms-theme-change", { detail: { theme } })
    );
  }, [theme]);

  const goTo = (callback, fallbackPath) => {
    if (typeof callback === "function") {
      callback();
      return;
    }
    // Fallback only; wire callbacks to your current auth state/routes when integrating.
    window.location.href = fallbackPath;
  };

  const t = (id, en) => language === "en" ? en : id;

  const navItems = [
    { label: t("Beranda", "Home"), href: "#beranda", active: true },
    { label: t("Fitur", "Features"), href: "#fitur" },
    { label: t("Template", "Templates"), href: "#template" },
    { label: t("Cara Kerja", "How It Works"), href: "#cara-kerja" },
    { label: t("Harga", "Plans"), href: "#harga" },
    { label: t("Bantuan", "Support"), href: "#bantuan" },
    { label: t("Tentang", "About"), href: "#tentang" },
    { label: t("Privasi", "Privacy"), href: "#privasi" },
  ];

  return (
    <>
    <main className={`pms-hero ${activeSection === "beranda" ? "" : "pms-hero--section-view"}`} id="beranda" data-theme={theme}>
      <div className="pms-hero__ambient pms-hero__ambient--blue" aria-hidden="true" />
      <div className="pms-hero__ambient pms-hero__ambient--purple" aria-hidden="true" />

      <svg
        className="pms-hero__light-trails"
        viewBox="0 0 1600 900"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="pmsOrbitBlue" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#1264ff" stopOpacity="0.12" />
            <stop offset="50%" stopColor="#00cfff" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#345cff" stopOpacity="0.55" />
          </linearGradient>
          <linearGradient id="pmsOrbitPurple" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#165dff" stopOpacity="0.15" />
            <stop offset="55%" stopColor="#6750ff" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#c044ff" stopOpacity="0.9" />
          </linearGradient>
          <filter id="pmsOrbitGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g fill="none" strokeLinecap="round">
          <circle cx="1500" cy="450" r="790"
            stroke="url(#pmsOrbitBlue)" strokeWidth="3"
            opacity="0.65" filter="url(#pmsOrbitGlow)" />
          <circle cx="1500" cy="450" r="730"
            stroke="url(#pmsOrbitPurple)" strokeWidth="2"
            opacity="0.72" />
          {Array.from({ length: 13 }, (_, i) => (
            <circle
              key={i}
              cx="1500"
              cy="450"
              r={340 + i * 37}
              stroke={i % 3 === 0 ? "url(#pmsOrbitPurple)" : "url(#pmsOrbitBlue)"}
              strokeWidth={i % 4 === 0 ? 1.25 : 0.8}
              opacity={i % 2 === 0 ? 0.65 : 0.4}
            />
          ))}
        </g>
      </svg>

      <header className="pms-header">
        <a className="pms-brand" href="#beranda" aria-label="PIN Maker Studio — Beranda">
          <img src={logo} alt="" className="pms-brand__logo" />
          <span className="pms-brand__name">
            PIN Maker <span>Studio</span>
          </span>
        </a>

        <nav className="pms-nav" aria-label="Navigasi utama">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className={activeSection === item.href.slice(1) ? "pms-nav__link is-active" : "pms-nav__link"}
              onClick={(event) => {
                event.preventDefault();
                navigateSection(item.href.slice(1));
              }}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="pms-header__actions">
          <div className="pms-preference-controls">
            <button
              type="button"
              className="pms-preference-button pms-language-button"
              onClick={() => setLanguage(current => current === "id" ? "en" : "id")}
              title={language === "id" ? "Switch to English" : "Ganti ke Bahasa Indonesia"}
              aria-label={language === "id" ? "Switch to English" : "Ganti ke Bahasa Indonesia"}
            >
              {language === "id" ? "ID" : "EN"}
            </button>
            <button
              type="button"
              className="pms-preference-button pms-theme-button"
              onClick={() => setTheme(current => current === "dark" ? "light" : "dark")}
              title={theme === "dark" ? t("Aktifkan tema terang", "Switch to light theme") : t("Aktifkan tema gelap", "Switch to dark theme")}
              aria-label={theme === "dark" ? t("Aktifkan tema terang", "Switch to light theme") : t("Aktifkan tema gelap", "Switch to dark theme")}
            >
              {theme === "dark" ? (
              <svg
                className="pms-theme-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2 M12 20v2 M4.93 4.93l1.42 1.42 M17.65 17.65l1.42 1.42 M2 12h2 M20 12h2 M4.93 19.07l1.42-1.42 M17.65 6.35l1.42-1.42" />
              </svg>
            ) : (
              <svg
                className="pms-theme-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6.5 6.5 0 0 0 8.268 8.268c.344-.215.825-.003.803.401Z" />
              </svg>
            )}
            </button>
          </div>
          <button
            type="button"
            className="pms-button pms-button--nav"
            onClick={() => goTo(onLogin, "/login")}
          >
            {t("Masuk", "Sign in")}
          </button>
          <button
            type="button"
            className="pms-button pms-button--nav-primary"
            onClick={() => goTo(onRegister, "/register")}
          >
            {t("Daftar", "Register")}
          </button>
        </div>
      </header>

      {activeSection === "beranda" && (
<section className="pms-hero__content" aria-labelledby="pms-hero-title">
        <div className="pms-hero__copy">
          <h1 id="pms-hero-title">
            {t("Buat Pin Custom", "Create Custom Pins")}
            <br />
            {t("Lebih Mudah", "Made Easier")}
            <br />
            <span>PIN Maker Studio</span>
          </h1>
          <p className="pms-hero__description">
            {t("Desain pin custom dengan editor yang praktis,", "Design custom pins with an intuitive editor,")}
            <br className="pms-desktop-break" /> {t("preset ukuran otomatis, dan hasil siap cetak", "automatic size presets, and print-ready results")}
            <br className="pms-desktop-break" /> {t("dalam hitungan menit.", "in minutes.")}
          </p>
          <div className="pms-hero__actions">
            <button
              type="button"
              className="pms-button pms-button--primary pms-start-button"
              onClick={() => goTo(onStart || onLogin, "/login")}
            >
              <span className="pms-start-button-label">
                {t("Mulai Sekarang", "Get Started")}
              </span>
            </button>
            <button
              type="button"
              className="pms-button pms-button--outline"
              onClick={() => navigateSection("template")}
            >
              {t("Pilihan Template", "Browse Templates")} <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </section>
)}
    
      {/* PMS_SITE_COPYRIGHT_HOME_V1 */}
      {activeSection === "beranda" && (
        <footer className="pms-site-footer pms-site-footer--home">
          <p className="pms-site-footer__main">
            © {new Date().getFullYear()} PIN Maker Studio.{" "}
            {t("Semua hak dilindungi undang-undang.", "All rights reserved.")}
          </p>
          <p className="pms-site-footer__legal">
            <a
              href="#privasi"
              onClick={(event) => {
                event.preventDefault();
                navigateSection("privasi");
              }}
            >
              {t("Kebijakan Privasi", "Privacy Policy")}
            </a>
            <span aria-hidden="true"> · </span>
            <a
              href="#persyaratan"
              onClick={(event) => {
                event.preventDefault();
                navigateSection("persyaratan");
              }}
            >
              {t("Persyaratan Layanan", "Terms of Service")}
            </a>
          </p>

        </footer>
      )}
</main>

    {/* PMS_NAV_SECTIONS_V1 */}
    <div className="pms-info-page" data-theme={theme}>
      {activeSection === "fitur" && (
<section id="fitur" className="pms-info-section">
        <div className="pms-info-inner">
          <p className="pms-info-eyebrow">{t("FITUR UTAMA", "KEY FEATURES")}</p>
          <h2>{t("Semua kebutuhan desain pin dalam satu tempat", "Everything you need to design pins in one place")}</h2>
          <p className="pms-info-lead">{t("PIN Maker Studio menyediakan alat bantu untuk menyusun desain pin custom dalam satu alur kerja. Melalui editor, kamu dapat mengatur elemen visual, meninjau komposisi desain, dan menyesuaikan rancangan sesuai kebutuhan proyek. Setiap fitur mendukung proses kerja yang lebih teratur, dari menentukan ukuran sampai mempersiapkan hasil desain.", "PIN Maker Studio provides tools for creating custom pin designs in one workflow. With the editor, you can arrange visual elements, review the composition, and refine your design to suit each project. These features support a more organized process, from selecting a size to preparing the final design.")}</p>
          <div className="pms-info-grid">
            <article className="pms-info-card">
              <span className="pms-info-number">01</span>
              <h3>{t("Editor PIN praktis", "Easy PIN editor")}</h3>
              <p>{t("Gunakan editor untuk menyusun elemen desain pin, mengatur posisi, dan meninjau tampilan rancangan. Kamu dapat memperhatikan keseimbangan teks dan elemen visual sebelum melanjutkan ke tahap berikutnya.", "Use the editor to arrange pin design elements, adjust their placement, and review the layout. Check the balance between text and visuals before moving to the next step.")}</p>
            </article>
            <article className="pms-info-card">
              <span className="pms-info-number">02</span>
              <h3>{t("Preset ukuran", "Size presets")}</h3>
              <p>{t("Pilih preset ukuran yang sesuai dengan kebutuhan proyek, mulai dari 25 mm hingga 75 mm. Pemilihan ukuran sejak awal membantu kamu menyesuaikan komposisi dan merencanakan desain dengan lebih baik.", "Choose a size preset for your project, from 25 mm to 75 mm. Selecting the size early helps you adjust the composition and plan your design more effectively.")}</p>
            </article>
            <article className="pms-info-card">
              <span className="pms-info-number">03</span>
              <h3>{t("Ekspor siap cetak", "Print-ready export")}</h3>
              <p>{t("Setelah selesai mendesain, periksa kembali ukuran, posisi elemen, dan keterbacaan teks. Kemudian gunakan fitur ekspor yang tersedia untuk menyiapkan hasil sesuai kebutuhan proses produksi.", "After designing, review the dimensions, element placement, and text readability. Then use the available export feature to prepare the output for your production workflow.")}</p>
            </article>
          </div>
        </div>
      </section>
)}

{activeSection === "template" && (
        <section id="template" className="pms-info-section">
          <div className="pms-info-inner pms-template-content">
            <p className="pms-info-eyebrow">{t("TEMPLATE PIN", "PIN TEMPLATES")}</p>
            <h2>{t("Pilih ukuran pin sesuai kebutuhan", "Choose a pin size for your project")}</h2>
            <p className="pms-info-lead">
              {t(
                "Pilih ukuran di bawah ini untuk melihat pilihan template pin. Kamu bisa mengganti pilihan sebelum melanjutkan ke aplikasi.",
                "Choose a size below to explore pin templates. You can change your selection before continuing to the app."
              )}
            </p>

            <div className="pms-template-grid">
              {[
                { size: "25", description: t("Ringkas dan minimalis", "Compact and minimal") },
                { size: "32", description: t("Untuk desain sederhana", "For simple designs") },
                { size: "44", description: t("Ukuran serbaguna", "Versatile size") },
                { size: "58", description: t("Area desain lebih luas", "More design space") },
                { size: "75", description: t("Ukuran ekstra besar", "Extra-large size") },
              ].map((item) => (
                <button
                  type="button"
                  key={item.size}
                  className={
                    selectedTemplate === item.size
                      ? "pms-template-card is-selected"
                      : "pms-template-card"
                  }
                  onClick={() => setSelectedTemplate(item.size)}
                  aria-pressed={selectedTemplate === item.size}
                >
                  <span className={`pms-template-preview pms-template-preview--${item.size}`} aria-hidden="true">
                    <span className={`pms-template-pin pms-template-pin--${item.size}`}>
                      {item.size} mm
                    </span>
                  </span>
                  <strong className="pms-template-size">{item.size} mm</strong>
                  <span className="pms-template-description">{item.description}</span>
                  <span className="pms-template-selection">
                    {selectedTemplate === item.size
                      ? t("Dipilih", "Selected")
                      : t("Pilih ukuran", "Select size")}
                  </span>
                </button>
              ))}
            </div>

            <div className="pms-template-footer">
              <p>
                {t("Ukuran yang dipilih:", "Selected size:")}{" "}
                <strong>{selectedTemplate} mm</strong>
              </p>
              <button
                type="button"
                className="pms-button pms-button--primary"
                onClick={() => {
                  try {
                    window.localStorage.setItem(
                      "pms-selected-template-size",
                      selectedTemplate
                    );
                  } catch {}
                  goTo(onStart || onLogin, "/login");
                }}
              >
                {t("Gunakan ukuran", "Use size")} {selectedTemplate} mm
                <span aria-hidden="true"> →</span>
              </button>
            </div>
          </div>
        </section>
)}

      {activeSection === "cara-kerja" && (
<section id="cara-kerja" className="pms-info-section">
        <div className="pms-info-inner">
          <p className="pms-info-eyebrow">{t("CARA KERJA", "HOW IT WORKS")}</p>
          <h2>{t("Dari ide menjadi desain pin", "From idea to pin design")}</h2>
          <p className="pms-info-lead">{t("Proses desain dilakukan melalui tiga tahap utama. Mulailah dengan masuk ke akun, lanjutkan dengan menyusun desain sesuai kebutuhan, lalu periksa kembali hasilnya sebelum menyiapkan file untuk produksi. Dengan mengikuti tahapan ini, pekerjaan dapat dilakukan secara lebih jelas dan terorganisasi.", "The design process follows three main steps. Sign in to your account, create a design that suits your needs, and review the result before preparing the file for production. Following these steps helps keep your workflow clear and organized.")}</p>
          <div className="pms-info-grid">
            <article className="pms-info-card">
              <span className="pms-info-number">01</span>
              <h3>{t("Masuk ke akun", "Sign in to your account")}</h3>
              <p>{t("Masuk menggunakan akun PIN Maker Studio atau buat akun baru jika belum memilikinya. Setelah berhasil masuk, lanjutkan ke editor untuk mulai mengerjakan rancangan pin.", "Sign in to PIN Maker Studio or create an account if you do not have one yet. Once signed in, continue to the editor to begin working on your pin design.")}</p>
            </article>
            <article className="pms-info-card">
              <span className="pms-info-number">02</span>
              <h3>{t("Atur desain", "Customize your design")}</h3>
              <p>{t("Tentukan ukuran pin, tambahkan teks atau elemen visual yang dibutuhkan, lalu atur posisi dan komposisinya. Tinjau desain secara berkala agar setiap elemen tersusun dengan baik.", "Choose a pin size, add the required text or visual elements, and adjust their placement and composition. Review the design as you work to keep its elements well arranged.")}</p>
            </article>
            <article className="pms-info-card">
              <span className="pms-info-number">03</span>
              <h3>{t("Siapkan hasil", "Prepare your result")}</h3>
              <p>{t("Periksa ukuran, posisi elemen, komposisi, dan keterbacaan teks. Jika sudah sesuai, gunakan fitur ekspor yang tersedia untuk menyiapkan hasil sebelum digunakan dalam proses produksi.", "Review the size, element placement, composition, and text readability. When everything looks right, use the available export feature to prepare the result for production.")}</p>
            </article>
          </div>
        </div>
      </section>
)}

      {activeSection === "harga" && (
<section id="harga" className="pms-info-section">
        <div className="pms-info-inner">
          <p className="pms-info-eyebrow">{t("PAKET", "PLANS")}</p>
          <h2>{t("Pilih layanan sesuai kebutuhan", "Choose a plan for your needs")}</h2>
          <p className="pms-info-lead">
            {t("Masuk ke aplikasi untuk melihat pilihan layanan dan informasi paket yang tersedia.", "Sign in to view available services and plan information.")}
          </p>
          <button
            type="button"
            className="pms-button pms-button--primary"
            onClick={() => goTo(onLogin, "/login")}
          >
            {t("Masuk ke PIN Maker Studio", "Sign in to PIN Maker Studio")}
          </button>
        </div>
      </section>
)}

      {activeSection === "bantuan" && (
<section id="bantuan" className="pms-info-section">
        <div className="pms-info-inner pms-help-content">
          <p className="pms-info-eyebrow">{t("BANTUAN", "SUPPORT")}</p>
          <h2>{t("Butuh Bantuan?", "Need help?")}</h2>
          <p className="pms-info-lead">
            {t("Hubungi tim PIN Maker Studio melalui kontak berikut.", "Contact the PIN Maker Studio team using the options below.")}
          </p>

          <div className="pms-contact-grid">
            <a
              className="pms-contact-card"
              href="https://wa.me/6281545901447"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Hubungi PIN Maker Studio melalui WhatsApp"
            >
              <span className="pms-contact-icon pms-contact-icon--whatsapp" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 11.5a8.4 8.4 0 0 1-12.4 7.4L4 20l1.1-3.4A8.4 8.4 0 1 1 20 11.5Z" />
                  <path d="M8.5 8.5c.3 2.2 2.8 4.7 5 5l1.2-1.2 2 1c-.2 1.1-1 1.8-2.2 1.8-3.7-.2-7.4-3.9-7.6-7.6 0-1.2.7-2 1.8-2.2l1 2-1.2 1.2Z" />
                </svg>
              </span>
              <span className="pms-contact-copy">
                <strong>WhatsApp</strong>
                <small>{t("Chat langsung dengan tim bantuan", "Chat with our support team")}</small>
                <span className="pms-contact-value">+62 815-4590-1447</span>
              </span>
              <span className="pms-contact-arrow" aria-hidden="true">↗</span>
            </a>

            <a
              className="pms-contact-card"
              href="https://mail.google.com/mail/?view=cm&fs=1&to=fr.digitalprintingstudio@gmail.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Hubungi PIN Maker Studio melalui email"
            >
              <span className="pms-contact-icon pms-contact-icon--email" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="m4 7 8 6 8-6" />
                </svg>
              </span>
              <span className="pms-contact-copy">
                <strong>Email</strong>
                <small>{t("Kirim pertanyaan melalui email", "Send us an email")}</small>
                <span className="pms-contact-value">fr.digitalprintingstudio@gmail.com</span>
              </span>
              <span className="pms-contact-arrow" aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      </section>
)}

      {activeSection === "tentang" && (
        <section id="tentang" className="pms-info-section">
          <div className="pms-info-inner pms-about-content">
            <p className="pms-info-eyebrow">{t("TENTANG", "ABOUT")}</p>
            <h2>{t("Mengenal PIN Maker Studio Lebih Dekat", "Get to Know PIN Maker Studio")}</h2>

            <p className="pms-info-lead">
              {t(
                "PIN Maker Studio adalah aplikasi yang dirancang untuk membantu proses pembuatan desain pin custom menjadi lebih praktis, terarah, dan mudah dipahami. Dari ide awal hingga persiapan hasil desain, aplikasi ini menyediakan ruang kerja digital untuk menyusun dan menyesuaikan rancangan pin.",
                "PIN Maker Studio is designed to make custom pin design more practical, organized, and accessible. From the initial idea to preparing the final design, the application provides a digital workspace for creating and refining pin layouts."
              )}
            </p>

            <div className="pms-about-article">
              <h3>{t("Berawal dari sebuah ide", "It Starts with an Idea")}</h3>
              <p>
                {t(
                  "Setiap desain pin memiliki tujuan dan karakter tersendiri. Ada yang dibuat untuk komunitas, organisasi, acara sekolah, identitas usaha, kenang-kenangan, hingga kebutuhan promosi. Proses mendesain yang baik membantu menyampaikan identitas tersebut melalui perpaduan teks, warna, bentuk, dan elemen visual yang sesuai.",
                  "Every pin design has its own purpose and character. Pins may be created for communities, organizations, school events, business identities, souvenirs, or promotional activities. A thoughtful design helps communicate that identity through a balanced combination of text, color, shapes, and visual elements."
                )}
              </p>

              <p>
                {t(
                  "PIN Maker Studio membantu pengguna menyusun ide tersebut ke dalam rancangan digital. Pengguna dapat mengatur komposisi, menambahkan elemen, dan meninjau tampilan desain melalui editor yang tersedia. Dengan alur kerja yang lebih terorganisasi, proses penyesuaian desain dapat dilakukan secara bertahap.",
                  "PIN Maker Studio helps users turn those ideas into digital layouts. You can arrange compositions, add elements, and review the design using the available editor. With a more organized workflow, design adjustments can be made step by step."
                )}
              </p>

              <h3>{t("Mendukung proses desain yang lebih praktis", "A More Practical Design Workflow")}</h3>
              <p>
                {t(
                  "Setiap kebutuhan produksi bisa memiliki ukuran dan susunan desain yang berbeda. Karena itu, pengaturan desain dan preset ukuran menjadi bagian penting dalam proses kerja. Pengguna dapat memilih pengaturan yang sesuai, menyesuaikan elemen visual, kemudian memeriksa kembali hasilnya sebelum melanjutkan ke tahap berikutnya.",
                  "Different production needs may require different sizes and layouts. That is why design settings and size presets play an important role in the workflow. Users can select suitable settings, adjust visual elements, and review their work before moving to the next step."
                )}
              </p>

              <p>
                {t(
                  "Antarmuka yang terarah juga membantu pengguna menemukan fungsi utama tanpa harus melalui langkah yang berlebihan. Baik saat menyusun konsep baru maupun memperbaiki rancangan yang sudah ada, fokusnya adalah membuat proses pengaturan desain terasa lebih nyaman dan efisien.",
                  "A clear interface also helps users find key functions without unnecessary steps. Whether creating a new concept or refining an existing layout, the goal is to make the design process more comfortable and efficient."
                )}
              </p>

              <h3>{t("Dari rancangan menuju persiapan cetak", "From Design to Print Preparation")}</h3>
              <p>
                {t(
                  "Setelah desain selesai disusun, penting untuk memeriksa kembali ukuran, posisi elemen, keterbacaan teks, warna, dan komposisi secara keseluruhan. Pemeriksaan ini membantu mengurangi kekeliruan sebelum file digunakan untuk kebutuhan produksi.",
                  "Once the design is arranged, it is important to review dimensions, element placement, text readability, colors, and the overall composition. This review helps reduce mistakes before the file is used for production."
                )}
              </p>

              <p>
                {t(
                  "PIN Maker Studio menyediakan alur kerja untuk membantu menyiapkan desain dan menggunakan fitur ekspor yang tersedia. Hasil akhirnya tetap perlu diperiksa sesuai kebutuhan bahan, mesin, ukuran produk, dan standar cetak yang digunakan. Dengan demikian, desain digital dapat dipersiapkan secara lebih terencana.",
                  "PIN Maker Studio provides a workflow for preparing designs and using the available export features. The final output should still be checked against the materials, equipment, product dimensions, and printing standards being used. This helps make digital designs more carefully prepared for production."
                )}
              </p>

              <h3>{t("Apa yang ingin kami bantu?", "What We Aim to Help With")}</h3>
              <p>
                {t(
                  "Kami ingin menyediakan alat bantu desain yang dapat digunakan untuk mendukung berbagai kebutuhan pembuatan pin custom. Pengaturan yang praktis, fitur editor, dan pilihan ukuran dirancang untuk membantu pengguna mengerjakan rancangan dengan lebih teratur, dari tahap awal sampai persiapan hasil.",
                  "We aim to provide a design tool that supports a variety of custom pin projects. Practical settings, editing features, and size options are intended to help users work more systematically, from the initial concept to preparing the final output."
                )}
              </p>

              <p>
                {t(
                  "Pada akhirnya, desain yang baik berasal dari ide yang jelas, penataan elemen yang tepat, dan pemeriksaan yang teliti. PIN Maker Studio hadir untuk membantu proses tersebut menjadi lebih mudah dikelola, sehingga pengguna dapat lebih fokus pada kreativitas dan kebutuhan desainnya.",
                  "Ultimately, good design comes from a clear idea, thoughtful arrangement of elements, and careful review. PIN Maker Studio is here to make that process easier to manage, allowing users to focus more on creativity and their design goals."
                )}
              </p>
            </div>

            <div className="pms-info-grid pms-about-highlights">
              <article className="pms-info-card">
                <span className="pms-info-number">01</span>
                <h3>{t("Fokus pada desain", "Design Focus")}</h3>
                <p>
                  {t(
                    "Susun teks, elemen visual, dan komposisi desain dalam satu alur kerja.",
                    "Arrange text, visual elements, and design compositions in one workflow."
                  )}
                </p>
              </article>
              <article className="pms-info-card">
                <span className="pms-info-number">02</span>
                <h3>{t("Pengaturan ukuran", "Size Settings")}</h3>
                <p>
                  {t(
                    "Manfaatkan preset ukuran dan sesuaikan pengaturan dengan kebutuhan desain.",
                    "Use size presets and adjust settings to suit your design requirements."
                  )}
                </p>
              </article>
              <article className="pms-info-card">
                <span className="pms-info-number">03</span>
                <h3>{t("Persiapan hasil", "Output Preparation")}</h3>
                <p>
                  {t(
                    "Tinjau kembali rancangan sebelum menggunakan hasilnya untuk kebutuhan cetak.",
                    "Review your design before using the output for printing."
                  )}
                </p>
              </article>
            </div>
          </div>
      </section>
)}

      {activeSection === "privasi" && (
<section id="privasi" className="pms-info-section">
        <div className="pms-info-inner pms-privacy-content">
          <p className="pms-info-eyebrow">{t("PRIVASI", "PRIVACY")}</p>
          <h2>Privasi akun Anda</h2>
          <p className="pms-info-lead">
            Informasi yang Anda masukkan digunakan untuk menjalankan fitur yang diminta,
            seperti pembuatan akun dan autentikasi. Saat menggunakan proses checkout,
            aplikasi juga meminta nama, email, dan nomor telepon.
          </p>
          <p>
            Jaga kerahasiaan kata sandi, token, dan API Key. Untuk pertanyaan atau
            permintaan terkait akun dan data, hubungi pengelola melalui kanal bantuan resmi.
          </p>
          <p className="pms-privacy-note">
            Informasi ini merupakan ringkasan privasi. Penjelasan lengkap mengenai
            pengelolaan, penyimpanan, dan penghapusan data perlu ditetapkan oleh pengelola layanan.
          </p>
        </div>
      </section>
)}
      {/* PMS_TERMS_SECTION_V1 */}
      {activeSection === "persyaratan" && (
        <section id="persyaratan" className="pms-info-section">
          <div className="pms-info-inner pms-privacy-content">
            <p className="pms-info-eyebrow">{t("PERSYARATAN LAYANAN", "TERMS OF SERVICE")}</p>
            <h2>{t("Persyaratan Layanan PIN Maker Studio", "PIN Maker Studio Terms of Service")}</h2>
            <p className="pms-info-lead">
              {t(
                "Dengan menggunakan PIN Maker Studio, Anda setuju untuk menggunakan aplikasi secara wajar, menjaga keamanan informasi akun, dan mematuhi ketentuan yang berlaku.",
                "By using PIN Maker Studio, you agree to use the application responsibly, protect your account information, and follow the applicable terms."
              )}
            </p>

            <h3>{t("Penggunaan akun", "Account Use")}</h3>
            <p>
              {t(
                "Anda bertanggung jawab atas kerahasiaan kata sandi, token, dan API Key yang terkait dengan akun Anda. Jangan membagikan informasi autentikasi kepada orang lain. Beri tahu pengelola layanan jika Anda menduga akun digunakan tanpa izin.",
                "You are responsible for keeping your password, tokens, and account API keys confidential. Do not share authentication information with others. Contact the service operator if you suspect unauthorized account access."
              )}
            </p>

            <h3>{t("Desain dan hasil ekspor", "Designs and Exports")}</h3>
            <p>
              {t(
                "Pastikan Anda memiliki hak untuk menggunakan teks, gambar, logo, dan aset lain yang dimasukkan ke dalam desain. Periksa kembali ukuran, tata letak, serta hasil ekspor sebelum digunakan untuk produksi. Hasil cetak dapat bergantung pada bahan, perangkat, dan pengaturan produksi yang digunakan.",
                "Make sure you have the rights to use any text, images, logos, or other assets included in your designs. Review dimensions, layout, and exported files before production. Printed results may depend on the materials, equipment, and production settings used."
              )}
            </p>

            <h3>{t("Pembayaran dan paket", "Payments and Plans")}</h3>
            <p>
              {t(
                "Informasi harga, durasi paket, status pembayaran, dan ketentuan pembelian yang ditampilkan saat transaksi menjadi acuan untuk transaksi tersebut. Simpan bukti transaksi dan hubungi bantuan resmi jika ada masalah pembayaran.",
                "Prices, plan durations, payment status, and purchase conditions displayed during checkout apply to that transaction. Keep your transaction records and contact official support if payment problems occur."
              )}
            </p>

            <h3>{t("Penggunaan yang dilarang", "Prohibited Use")}</h3>
            <p>
              {t(
                "Jangan menggunakan aplikasi untuk aktivitas yang melanggar hukum, merugikan pihak lain, mengganggu keamanan sistem, atau mencoba memperoleh akses tanpa izin. Pengelola dapat meninjau dugaan penyalahgunaan sesuai ketentuan yang berlaku.",
                "Do not use the application for unlawful activity, to harm others, to compromise system security, or to obtain unauthorized access. The service operator may review suspected misuse in accordance with applicable rules."
              )}
            </p>

            <h3>{t("Perubahan ketentuan dan kontak", "Changes and Contact")}</h3>
            <p>
              {t(
                "Ketentuan ini perlu ditinjau dan diperbarui apabila layanan berubah. Untuk pertanyaan mengenai akun, transaksi, atau penggunaan aplikasi, silakan gunakan kanal bantuan resmi yang tercantum pada halaman Bantuan.",
                "These terms should be reviewed and updated when the service changes. For questions about accounts, transactions, or application use, please use the official contact channels listed on the Support page."
              )}
            </p>
            <p className="pms-privacy-note">
              {t(
                "Halaman ini merupakan draf ketentuan umum dan perlu ditinjau oleh pengelola sebelum dijadikan ketentuan hukum final.",
                "This page is a general draft and should be reviewed by the service operator before being adopted as final legal terms."
              )}
            </p>
          </div>
        </section>
      )}

      {/* PMS_SITE_COPYRIGHT_SECTION_V1 */}
      {activeSection !== "beranda" && (
        <footer className="pms-site-footer pms-site-footer--section">
          <p className="pms-site-footer__main">
            © {new Date().getFullYear()} PIN Maker Studio.{" "}
            {t("Semua hak dilindungi undang-undang.", "All rights reserved.")}
          </p>
          <p className="pms-site-footer__legal">
            <a
              href="#privasi"
              onClick={(event) => {
                event.preventDefault();
                navigateSection("privasi");
              }}
            >
              {t("Kebijakan Privasi", "Privacy Policy")}
            </a>
            <span aria-hidden="true"> · </span>
            <a
              href="#persyaratan"
              onClick={(event) => {
                event.preventDefault();
                navigateSection("persyaratan");
              }}
            >
              {t("Persyaratan Layanan", "Terms of Service")}
            </a>
          </p>

        </footer>
      )}
    </div>

      {/* PMS_COOKIE_CONSENT_BANNER_V1 */}
      {cookieConsent === null && (
        <aside
          className="pms-cookie-banner"
          role="dialog"
          aria-label={t("Pengaturan cookie dan privasi", "Cookie and privacy settings")}
        >
          <div className="pms-cookie-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.2 13.2A8.7 8.7 0 1 1 10.8 3.8a5.3 5.3 0 0 0 9.4 9.4Z" />
              <circle cx="8.1" cy="9" r=".8" fill="currentColor" />
              <circle cx="12" cy="14" r=".8" fill="currentColor" />
              <circle cx="7.5" cy="15" r=".8" fill="currentColor" />
              <circle cx="12.5" cy="8" r=".8" fill="currentColor" />
            </svg>
          </div>
          <div className="pms-cookie-copy">
            <strong>{t("COOKIE & PRIVACY SETTINGS", "COOKIE & PRIVACY SETTINGS")}</strong>
            <p>
              {t(
                "Kami menyimpan pilihan persetujuan di browser dan menggunakan penyimpanan yang diperlukan untuk fungsi dasar situs. Pilih apakah Anda menyetujui penggunaan cookie opsional.",
                "We save your consent choice in this browser and use essential storage for core site functions. Choose whether you consent to optional cookies."
              )}
              {" "}
              <a
                href="#privasi"
                onClick={(event) => {
                  event.preventDefault();
                  navigateSection("privasi");
                }}
              >
                {t("Baca Kebijakan Privasi", "Read the Privacy Policy")}
              </a>
              .
            </p>
          </div>
          <div className="pms-cookie-actions">
            <button
              type="button"
              className="pms-cookie-button pms-cookie-button--essential"
              onClick={() => saveCookieConsent("essential")}
            >
              {t("Hanya Cookie Esensial", "Essential Cookies Only")}
            </button>
            <button
              type="button"
              className="pms-cookie-button pms-cookie-button--accept"
              onClick={() => saveCookieConsent("all")}
            >
              {t("Accept All Cookies", "Accept All Cookies")}
            </button>
          </div>
        </aside>
      )}

    </>
  );
}
